import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { ControlPoint } from '@shared/models/controlPoint';
import { Site } from '@shared/models/site';
import { SitesHttpService } from '@shared/services/http/sites-http.service';
import { ControlPointsService } from '@features/control-points/services/control-points-service';
import { Seismograph } from '@shared/models/seismograph';
import { SeismographService } from '@features/seismograph-devices/services/seismograph-service';

export interface ReportControlPointSelectionData {
  siteId?: number;
  controlPoints?: ControlPoint[];
  devices?: Seismograph[];
  selectedDeviceIds?: number[];
  selectedDeviceSerialNumbers?: string[];
  lockDevicesSelection?: boolean;
  selectedControlPointIds?: number[];
  lockControlPointsSelection?: boolean;
  eventId?: number;
  eventName?: string;
  eventType?: string;
  location?: string;
  organisation?: string;
}

export interface ReportGenerationParams {
  controlPoints: { ControlPointId: number; ControlPointName: string }[];
  startDate: Date;
  endDate: Date;
  ponderationId?: number;
  eventName?: string;
  eventType?: string;
  location?: string;
  organisation?: string;
  devices?: string[];
}

@Component({
  selector: 'app-report-control-point-selection-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NgSelectModule
  ],
  templateUrl: './report-control-point-selection-modal.component.html',
  styleUrl: './report-control-point-selection-modal.component.scss'
})
export class ReportControlPointSelectionModalComponent implements OnInit {
  sites: Site[] = [];
  selectedSite: Site | null = null;

  allDevices: Seismograph[] = [];
  devices: Seismograph[] = [];
  selectedDevices: Seismograph[] = [];
  lockDevicesSelection = false;

  controlPoints: ControlPoint[] = [];
  selectedControlPoints: ControlPoint[] = [];
  lockControlPointsSelection = false;
  loadingControlPoints = false;
  private initializedSelections = false;

  reportTypes = ['Period'];
  selectedType = 'Period';

  startDate = '';
  endDate = '';

  formError = '';

  constructor(
    public dialogRef: MatDialogRef<ReportControlPointSelectionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportControlPointSelectionData,
    private sitesService: SitesHttpService,
    private controlPointsService: ControlPointsService,
    private seismographService: SeismographService
  ) {}

  ngOnInit(): void {
    this.lockDevicesSelection = !!this.data.lockDevicesSelection;
    this.lockControlPointsSelection = !!this.data.lockControlPointsSelection;

    this.loadDevices();

    this.sitesService.getSitesAcitve().subscribe(sites => {
      this.sites = sites;
      if (this.data.siteId) {
        this.selectedSite = sites.find(s => s.siteId === this.data.siteId) || null;
        if (this.selectedSite) {
          this.onSiteChange(this.selectedSite);
        }
      }
    });
  }

  private async loadDevices(): Promise<void> {
    if (this.data.devices && this.data.devices.length > 0) {
      this.allDevices = this.data.devices;
    } else {
      this.allDevices = await this.seismographService.getAllSeismographs();
    }

    this.updateDevicesForSite();
    this.applyInitialSelectedDevices();
  }

  private updateDevicesForSite(): void {
    if (!this.selectedSite?.siteId) {
      this.devices = [...this.allDevices];
      return;
    }

    this.devices = this.allDevices.filter(d => d.siteId === this.selectedSite?.siteId);
  }

  private applyInitialSelectedDevices(): void {
    const selectedByIds = this.data.selectedDeviceIds || [];
    const selectedBySerials = this.data.selectedDeviceSerialNumbers || [];

    if (selectedByIds.length > 0) {
      const selectedIdSet = new Set(selectedByIds);
      this.selectedDevices = this.allDevices.filter(d => selectedIdSet.has(d.id));
      return;
    }

    if (selectedBySerials.length > 0) {
      const selectedSerialSet = new Set(selectedBySerials.map(s => s.toLowerCase()));
      this.selectedDevices = this.allDevices.filter(d =>
        !!d.serialNumber && selectedSerialSet.has(d.serialNumber.toLowerCase())
      );
      return;
    }

    if (this.lockDevicesSelection && this.data.devices && this.data.devices.length > 0) {
      this.selectedDevices = [...this.data.devices];
    }
  }

  onSiteChange(site: Site | null): void {
    this.controlPoints = [];
    if (this.initializedSelections && !this.lockDevicesSelection) {
      this.selectedDevices = [];
    }
    if (this.initializedSelections && !this.lockControlPointsSelection) {
      this.selectedControlPoints = [];
    }

    this.updateDevicesForSite();

    if (!site?.siteId) return;

    this.loadingControlPoints = true;
    this.controlPointsService.getControlPointsBySiteId(site.siteId, false).subscribe({
      next: async (cps) => {
        this.controlPoints = cps;
        await this.applyInitialSelectedControlPoints();
        this.initializedSelections = true;
        this.loadingControlPoints = false;
      },
      error: () => { this.loadingControlPoints = false; }
    });
  }

  private async applyInitialSelectedControlPoints(): Promise<void> {
    const selectedIds = this.data.selectedControlPointIds || [];

    if (selectedIds.length === 0) {
      return;
    }

    const normalizedIds = selectedIds
      .map(id => Number(id))
      .filter(id => !Number.isNaN(id));
    const selectedIdSet = new Set(normalizedIds);

    this.selectedControlPoints = this.controlPoints.filter(cp => selectedIdSet.has(cp.controlPointId));

    if (this.selectedControlPoints.length === normalizedIds.length) {
      return;
    }

    const existingIds = new Set(this.controlPoints.map(cp => cp.controlPointId));
    const missingIds = normalizedIds.filter(id => !existingIds.has(id));

    if (missingIds.length === 0) {
      return;
    }

    const missingPoints = await Promise.all(
      missingIds.map(id => this.controlPointsService.getControlPointByIdAsync(id))
    );

    const hydratedPoints = missingPoints.filter((cp): cp is ControlPoint => !!cp);
    if (hydratedPoints.length === 0) {
      return;
    }

    this.controlPoints = [...this.controlPoints, ...hydratedPoints];
    this.selectedControlPoints = this.controlPoints.filter(cp => selectedIdSet.has(cp.controlPointId));
  }

  isFormValid(): boolean {
    return !!this.selectedSite
      && (this.selectedControlPoints.length > 0 || this.selectedDevices.length > 0)
      && !!this.startDate
      && !!this.endDate;
  }

  private buildDateTime(dateStr: string): Date | undefined {
    if (!dateStr) return undefined;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? undefined : d;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async onConfirm(): Promise<void> {
    this.formError = '';
    if (!this.isFormValid()) {
      this.formError = 'REPORT.FORM_INVALID';
      return;
    }

    const start = this.buildDateTime(this.startDate);
    const end = this.buildDateTime(this.endDate);
    if (!start || !end) {
      this.formError = 'REPORT.FORM_INVALID';
      return;
    }

    if (start && end && start > end) {
      this.formError = 'REPORT.DATE_RANGE_INVALID';
      return;
    }

    let effectiveControlPoints = [...this.selectedControlPoints];

    // if (this.selectedDevices.length > 0) {
    //   const selectedDeviceIds = this.selectedDevices.map(d => d.id).filter(Boolean);
    //   const controlPointInstruments = await this.seismographService.getControlPointInstrumentsBySeismographsId(selectedDeviceIds);
    //   const deviceControlPointIds = new Set(controlPointInstruments.map(i => i.controlPointId));

    //   if (effectiveControlPoints.length > 0) {
    //     effectiveControlPoints = effectiveControlPoints.filter(cp => deviceControlPointIds.has(cp.controlPointId));
    //   } else {
    //     effectiveControlPoints = this.controlPoints.filter(cp => deviceControlPointIds.has(cp.controlPointId));
    //   }
    // }

    // if (effectiveControlPoints.length === 0) {
    //   this.formError = 'REPORT.FORM_INVALID';
    //   return;
    // }

    const params: ReportGenerationParams = {
      controlPoints: effectiveControlPoints.map(cp => ({
        ControlPointId: cp.controlPointId,
        ControlPointName: cp.name
      })),
      startDate: start,
      endDate: end,
      eventName: this.data.eventName,
      eventType: this.data.eventType,
      location: this.selectedSite?.nom,
      organisation: this.data.organisation,
      devices: this.selectedDevices.map(d => d.serialNumber)
    };
    this.dialogRef.close(params);
  }
}
